/**
 * aura-build-cost.ts — WEB.4.UI.8b (R9 perk wiring, R11.2.6 discount picker)
 *
 * Pure helper that computes a facility's effective build cost given the
 * current AuraManagerState. Filters auras to:
 *   - scope = "BuildCost"
 *   - ownerSeat matches drawer (or -1 global)
 *   - categoryFilter matches facility.category (when set)
 *   - facilityFilter matches facility.id (case-insensitive, when set)
 *
 * Apply order: mult-then-add accumulators (matches rules-engine
 * BuildCostService). mult=0 is honored as a zero-override.
 *
 * R9 perk additive (applied AFTER aura resolution, before floor 0):
 *   Engineer  — build cost −2
 *   Activist  — build cost +2 (trade-off for pollution × 0.7)
 *
 * R11.2.6 discount picker:
 *   discountOverride = null    → auto-best (apply first matching free discount, current behavior)
 *   discountOverride = "skip"  → skip all PEQ discounts; auras + perks still apply
 *   discountOverride = PendingKind → apply only that specific discount kind (if matching entry exists)
 */

import type { AuraInstance, AuraManagerState, PendingEffectQueueState } from "@gdg/rules-engine";
import { applyModifier, getByScope, peekIncludingGlobal } from "@gdg/rules-engine";
import type { PendingKind } from "@gdg/shared";
import { FACILITIES } from "./facilities.js";

export interface AuraDiscount {
  /** sourceCardName from the AuraInstance */
  source: string;
  /** Multiplier applied (1 = identity, 0.5 = halve, 0 = override-to-zero) */
  mult: number;
  /** Additive applied (negative for discount) */
  add: number;
}

export interface BuildCostBreakdown {
  baseCost: number;
  effectiveCost: number;
  /** All BuildCost auras that matched this facility (for display). */
  matched: AuraDiscount[];
  /**
   * R11.2.6 codex HIGH 1 fix — the PendingKind that was ACTUALLY applied to
   * compute this breakdown. BuildMenu must pass this back to buildFacility
   * so the store consumes the same PEQ entry it previewed. Null when no PEQ
   * discount was applied (skip path, or no eligible entry).
   */
  appliedDiscountKind: PendingKind | null;
}

/** R9 perk archetype — drives per-perk build cost additive. */
export type SeatPerkForBuild = "engineer" | "scientist" | "activist" | "politician";

/**
 * R11.2.6: sentinel value for discountOverride to mean "player chose to skip discount".
 * Exported so BuildMenu can use it as a typed sentinel without string literals.
 */
export const DISCOUNT_SKIP = "skip" as const;
export type DiscountOverride = PendingKind | typeof DISCOUNT_SKIP | null;

/**
 * Enumerate eligible PEQ discount entries for a given seat.
 * Returns all NextBuildFree / NextSolarFree / NextBuildDiscount / NextNuclearDiscount
 * entries visible to seatIndex (seat-local + global "-1").
 * Used by BuildMenu to populate the discount picker pill list.
 */
export function getEligibleDiscounts(
  peqState: PendingEffectQueueState,
  seatIndex: number,
): ReadonlyArray<import("@gdg/rules-engine").PendingEffect> {
  const pending = peekIncludingGlobal(peqState, seatIndex);
  return pending.filter(
    (pe) =>
      pe.kind === "NextBuildFree" ||
      pe.kind === "NextSolarFree" ||
      pe.kind === "NextBuildDiscount" ||
      pe.kind === "NextNuclearDiscount",
  );
}

/**
 * Compute the effective build cost for a facility given the current aura state.
 *
 * @param facilityId       — web-client facility id ("coal" / "oil" / etc.)
 * @param baseCost         — FacilityDef.baseCost
 * @param auraState        — store.auraState
 * @param seatIndex        — drawer's seat
 * @param perk             — R9 archetype perk (optional)
 * @param peqState         — pending effect queue state (optional)
 * @param discountOverride — R11.2.6: null=auto-best, "skip"=no discount, PendingKind=specific
 *
 * R9 perk additives (applied AFTER aura resolution):
 *   Engineer  — −2 tokens (cheaper builds)
 *   Activist  — +2 tokens (greener but costlier)
 */
export function computeBuildCostBreakdown(
  facilityId: string,
  baseCost: number,
  auraState: AuraManagerState,
  seatIndex: number,
  perk?: SeatPerkForBuild,
  peqState?: PendingEffectQueueState,
  discountOverride?: DiscountOverride,
): BuildCostBreakdown {
  const facility = FACILITIES.find((f) => f.id === facilityId);
  if (facility === undefined) {
    return { baseCost, effectiveCost: baseCost, matched: [], appliedDiscountKind: null };
  }

  // R11.1 fix #3 / R11.2.6 discount picker:
  // When discountOverride !== "skip", check PEQ for applicable discounts.
  // - null (auto-best): apply first matching NextBuildFree/NextSolarFree → cost 0
  // - specific PendingKind: apply only that kind (validate entry exists first)
  // - "skip": skip PEQ discounts entirely (player chose full price)
  if (peqState !== undefined && discountOverride !== DISCOUNT_SKIP) {
    const pending = peekIncludingGlobal(peqState, seatIndex);
    const isSolar = facility.id.toLowerCase().includes("solar");

    if (discountOverride == null) {
      // Auto-best: apply first matching free discount (R11.1 behavior preserved).
      for (const pe of pending) {
        if (pe.kind === "NextBuildFree" || (pe.kind === "NextSolarFree" && isSolar)) {
          const catFits =
            pe.categoryFilter == null ||
            pe.categoryFilter.toLowerCase() === facility.category.toLowerCase();
          const facFits =
            pe.facilityFilter == null ||
            pe.facilityFilter.toLowerCase() === facility.id.toLowerCase();
          if (catFits && facFits) {
            return {
              baseCost,
              effectiveCost: 0,
              matched: [{ source: pe.sourceCardName ?? "Next Build Free", mult: 0, add: 0 }],
              appliedDiscountKind: pe.kind,
            };
          }
        }
      }
    } else {
      // Specific discount kind: validate matching entry exists, then apply.
      const chosenKind = discountOverride;
      for (const pe of pending) {
        if (pe.kind !== chosenKind) continue;
        const catFits =
          pe.categoryFilter == null ||
          pe.categoryFilter.toLowerCase() === facility.category.toLowerCase();
        const facFits =
          pe.facilityFilter == null ||
          pe.facilityFilter.toLowerCase() === facility.id.toLowerCase();
        if (!catFits || !facFits) continue;
        // Matching entry found — apply discount.
        if (chosenKind === "NextBuildFree" || (chosenKind === "NextSolarFree" && isSolar)) {
          return {
            baseCost,
            effectiveCost: 0,
            matched: [{ source: pe.sourceCardName ?? "Next Build Free", mult: 0, add: 0 }],
            appliedDiscountKind: chosenKind,
          };
        }
        if (chosenKind === "NextBuildDiscount" || chosenKind === "NextNuclearDiscount") {
          // Discount is additive reduction applied AFTER aura stack.
          // Compute aura stack first, then apply reduction.
          break; // fall through to aura stack below
        }
        break;
      }
      // For discount kinds that reduce (not zero): fall through to aura stack
      // then subtract pe.valueInt. Handle below after aura resolution.
      if (chosenKind === "NextBuildDiscount" || chosenKind === "NextNuclearDiscount") {
        // Find the matching discount value
        let discountValue = 0;
        for (const pe of pending) {
          if (pe.kind !== chosenKind) continue;
          const catFits =
            pe.categoryFilter == null ||
            pe.categoryFilter.toLowerCase() === facility.category.toLowerCase();
          const facFits =
            pe.facilityFilter == null ||
            pe.facilityFilter.toLowerCase() === facility.id.toLowerCase();
          if (catFits && facFits) {
            discountValue = pe.valueInt;
            break;
          }
        }
        // Apply aura stack
        const auras = getByScope(auraState, "BuildCost", seatIndex);
        let mult = 1;
        let add = 0;
        const matched: AuraDiscount[] = [];
        for (const a of auras) {
          if (!auraMatchesFacility(a, facility.id, facility.category)) continue;
          const eMult = a.effect.mult !== undefined ? a.effect.mult : 1;
          const eAdd = a.effect.add ?? 0;
          mult *= eMult;
          add += eAdd;
          matched.push({ source: a.sourceCardName, mult: eMult, add: eAdd });
        }
        const { result: auraEffective } = applyModifier(baseCost, mult, add, "BuildMenu");
        let perkAdd = 0;
        if (perk === "engineer") perkAdd = -2;
        if (perk === "activist") perkAdd = 2;
        const effective = Math.max(0, Math.max(1, auraEffective + perkAdd) - discountValue);
        return { baseCost, effectiveCost: effective, matched, appliedDiscountKind: chosenKind };
      }
    }
  }

  const auras = getByScope(auraState, "BuildCost", seatIndex);
  let mult = 1;
  let add = 0;
  const matched: AuraDiscount[] = [];

  for (const a of auras) {
    if (!auraMatchesFacility(a, facility.id, facility.category)) continue;
    const eMult = a.effect.mult !== undefined ? a.effect.mult : 1;
    const eAdd = a.effect.add ?? 0;
    mult *= eMult;
    add += eAdd;
    matched.push({ source: a.sourceCardName, mult: eMult, add: eAdd });
  }

  // Codex R1 MED 1: use rules-engine applyModifier (Math.ceil) for Unity-parity
  // rounding. Math.round drifts on fractional aura mults (e.g. nuclear 25*0.4=10
  // ceils to 10, rounds to 10; nuclear 25*0.45=11.25 ceils to 12, rounds to 11).
  const { result: auraEffective } = applyModifier(baseCost, mult, add, "BuildMenu");

  // R9 perk: additive applied after aura resolution, floor 1 (can't build for free).
  let perkAdd = 0;
  if (perk === "engineer") perkAdd = -2;
  if (perk === "activist") perkAdd = 2;
  const effective = Math.max(1, auraEffective + perkAdd);

  return { baseCost, effectiveCost: effective, matched, appliedDiscountKind: null };
}

function auraMatchesFacility(
  aura: AuraInstance,
  facilityId: string,
  facilityCategory: string,
): boolean {
  // categoryFilter: case-insensitive match against facility.category when set.
  // (Codex R1 LOW 2: was case-sensitive; resolver canonicalizes to "Fossil"
  // etc. so runtime worked, but the helper's stated contract was violated.)
  if (aura.categoryFilter != null) {
    if (aura.categoryFilter.toLowerCase() !== facilityCategory.toLowerCase()) return false;
  }
  // facilityFilter: case-insensitive match against facility.id when set.
  if (aura.facilityFilter != null) {
    if (aura.facilityFilter.toLowerCase() !== facilityId.toLowerCase()) return false;
  }
  return true;
}
